import React, { useState } from 'react';
import { Container, Card, Button, Alert, ProgressBar, Badge, Form, InputGroup } from 'react-bootstrap';
import { db } from '../../firebase';
import { collection, getDocs, writeBatch, doc, addDoc } from 'firebase/firestore';

const SetupStudentTypes = () => {
	const [loading, setLoading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [results, setResults] = useState(null);
	const [error, setError] = useState(null);
	const [baseEmail, setBaseEmail] = useState('apicella.anthony@gmail.com');
	const [emailError, setEmailError] = useState('');

	// Helper function to create email with plus addressing
	const createPlusEmail = (base, suffix) => {
		if (!base) return `test+${suffix}@example.com`;
		const [localPart, domain] = base.split('@');
		return `${localPart}+${suffix}@${domain}`;
	};

	// Validate email format
	const validateEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const handleEmailChange = (e) => {
		const email = e.target.value;
		setBaseEmail(email);

		if (email && !validateEmail(email)) {
			setEmailError('Please enter a valid email address');
		} else {
			setEmailError('');
		}
	};

	// Generate fake student data based on base email
	const generateFakeStudents = (base) => [
		{
			firstName: 'Test',
			lastName: 'Student1',
			fullName: 'Test Student1',
			email: createPlusEmail(base, 'teststudent1'),
			phone: '555-0101',
			age: 10,
			instrument: 'Piano',
			school: 'Test Elementary',
			status: 'Active',
			isTestStudent: true,
			nextLesson: new Date('2025-10-15T10:00:00').toISOString(),
			notes: 'This is a test student for development purposes.',
			parent1: {
				firstName: 'Parent',
				lastName: 'Test1',
				email: createPlusEmail(base, 'parent-test1'),
				phone: '555-0201',
			}
		},
		{
			firstName: 'Demo',
			lastName: 'Johnson',
			fullName: 'Demo Johnson',
			email: createPlusEmail(base, 'demostudent'),
			phone: '555-0102',
			age: 12,
			instrument: 'Guitar',
			school: 'Demo Middle School',
			status: 'Active',
			isTestStudent: true,
			nextLesson: new Date('2025-10-16T14:00:00').toISOString(),
			notes: 'Another test student for development.',
			parent1: {
				firstName: 'Parent',
				lastName: 'Demo',
				email: createPlusEmail(base, 'parent-demo'),
				phone: '555-0202',
			}
		},
		{
			firstName: 'Sample',
			lastName: 'Williams',
			fullName: 'Sample Williams',
			email: createPlusEmail(base, 'samplestudent'),
			phone: '555-0103',
			age: 8,
			instrument: 'Violin',
			school: 'Sample Elementary',
			status: 'Active',
			isTestStudent: true,
			nextLesson: new Date('2025-10-17T16:00:00').toISOString(),
			notes: 'Test student for feature testing.',
			parent1: {
				firstName: 'Parent',
				lastName: 'Sample',
				email: createPlusEmail(base, 'parent-sample'),
				phone: '555-0203',
			}
		},
		{
			firstName: 'Fake',
			lastName: 'Davis',
			fullName: 'Fake Davis',
			email: createPlusEmail(base, 'fakestudent'),
			phone: '555-0104',
			age: 15,
			instrument: 'Drums',
			school: 'Fake High School',
			status: 'Active',
			isTestStudent: true,
			nextLesson: new Date('2025-10-18T11:00:00').toISOString(),
			notes: 'Test student for batch operations.',
			parent1: {
				firstName: 'Parent',
				lastName: 'Fake',
				email: createPlusEmail(base, 'parent-fake'),
				phone: '555-0204',
			}
		},
		{
			firstName: 'Mock',
			lastName: 'Martinez',
			fullName: 'Mock Martinez',
			email: createPlusEmail(base, 'mockstudent'),
			phone: '555-0105',
			age: 9,
			instrument: 'Flute',
			school: 'Mock Elementary',
			status: 'Inactive',
			isTestStudent: true,
			notes: 'Inactive test student.',
			parent1: {
				firstName: 'Parent',
				lastName: 'Mock',
				email: createPlusEmail(base, 'parent-mock'),
				phone: '555-0205',
			}
		},
	];

	const fakeStudents = generateFakeStudents(baseEmail);

	const handleSetup = async () => {
		setLoading(true);
		setError(null);
		setProgress(0);
		const setupResults = {
			realStudentsMarked: 0,
			testStudentsCreated: 0,
			errors: [],
		};

		try {
			// Step 1: Mark existing students as real
			setProgress(25);
			const studentsSnapshot = await getDocs(collection(db, 'students'));
			const batch = writeBatch(db);
			let batchCount = 0;
			const BATCH_LIMIT = 500;

			for (const studentDoc of studentsSnapshot.docs) {
				const data = studentDoc.data();
				// Only update if isTestStudent is not already set
				if (data.isTestStudent === undefined) {
					batch.update(studentDoc.ref, { isTestStudent: false });
					batchCount++;
					setupResults.realStudentsMarked++;

					// Commit batch if we hit the limit
					if (batchCount >= BATCH_LIMIT) {
						await batch.commit();
						batchCount = 0;
					}
				}
			}

			// Commit any remaining updates
			if (batchCount > 0) {
				await batch.commit();
			}

			setProgress(50);

			// Step 2: Create test students
			for (let i = 0; i < fakeStudents.length; i++) {
				const student = fakeStudents[i];

				// Check if test student already exists by email
				const existingQuery = await getDocs(
					collection(db, 'students')
				);
				const exists = existingQuery.docs.some(
					doc => doc.data().email === student.email
				);

				if (!exists) {
					await addDoc(collection(db, 'students'), student);
					setupResults.testStudentsCreated++;
				}

				setProgress(50 + ((i + 1) / fakeStudents.length) * 50);
			}

			setProgress(100);
			setResults(setupResults);
		} catch (err) {
			console.error('Error setting up student types:', err);
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Container className='py-5'>
			<h1 className='mb-4' style={{ color: '#384971' }}>
				<i className='fa-sharp fa-solid fa-wrench me-2'></i>
				Setup Student Types
			</h1>

			<Card className='shadow-sm mb-4'>
				<Card.Body>
					<h5 className='mb-3'>What this tool does:</h5>
					<ol>
						<li className='mb-2'>
							<strong>Marks all existing students as REAL students</strong>
							<br />
							<small className='text-muted'>
								Adds <code>isTestStudent: false</code> to all students who don't have this field yet
							</small>
						</li>
						<li className='mb-2'>
							<strong>Creates 5 test students for development</strong>
							<br />
							<small className='text-muted'>
								Adds fake students with <code>isTestStudent: true</code> that you can use for testing features
							</small>
						</li>
					</ol>

					<Alert variant='info' className='mt-3 mb-0'>
						<i className='fa-sharp fa-solid fa-info-circle me-2'></i>
						<strong>Note:</strong> This is a one-time setup. You can run it again safely - it will skip students that already have the field set.
					</Alert>
				</Card.Body>
			</Card>

			{/* Gmail Plus Addressing Configuration */}
			<Card className='shadow-sm mb-4' style={{ borderLeft: '4px solid #4285f4' }}>
				<Card.Body>
					<h5 className='mb-3'>
						<i className='fa-sharp fa-solid fa-envelope me-2' style={{ color: '#4285f4' }}></i>
						Gmail Plus Addressing Setup
					</h5>
					<p className='mb-3'>
						Use Gmail's "plus addressing" feature to route all test emails to one inbox.
						Enter your Gmail address below and all test student emails will use variants like <code>yourname+teststudent1@gmail.com</code>
					</p>

					<Form.Group className='mb-3'>
						<Form.Label>Your Base Email Address</Form.Label>
						<InputGroup>
							<InputGroup.Text>
								<i className='fa-sharp fa-solid fa-at'></i>
							</InputGroup.Text>
							<Form.Control
								type='email'
								placeholder='your.email@gmail.com'
								value={baseEmail}
								onChange={handleEmailChange}
								isInvalid={!!emailError}
								disabled={loading}
							/>
							<Form.Control.Feedback type='invalid'>
								{emailError}
							</Form.Control.Feedback>
						</InputGroup>
						<Form.Text className='text-muted'>
							<strong>How it works:</strong> Gmail ignores everything after the "+" symbol, so
							<code>you+test1@gmail.com</code> and <code>you+test2@gmail.com</code> both deliver to <code>you@gmail.com</code>.
							This also works with Google Workspace (yourname@yourdomain.com).
						</Form.Text>
					</Form.Group>

					{baseEmail && !emailError && (
						<Alert variant='success' className='mb-0'>
							<i className='fa-sharp fa-solid fa-check-circle me-2'></i>
							<strong>Preview:</strong> All test emails will be sent to <strong>{baseEmail}</strong>
							<br />
							<small>Examples: {createPlusEmail(baseEmail, 'teststudent1')}, {createPlusEmail(baseEmail, 'parent-test1')}</small>
						</Alert>
					)}
				</Card.Body>
			</Card>

			{error && (
				<Alert variant='danger' className='mb-4'>
					<i className='fa-sharp fa-solid fa-exclamation-triangle me-2'></i>
					<strong>Error:</strong> {error}
				</Alert>
			)}

			{loading && (
				<Card className='shadow-sm mb-4'>
					<Card.Body>
						<h5 className='mb-3'>Setup in progress...</h5>
						<ProgressBar now={progress} label={`${Math.round(progress)}%`} animated />
					</Card.Body>
				</Card>
			)}

			{results && !loading && (
				<Card className='shadow-sm mb-4' style={{ borderLeft: '4px solid #28a745' }}>
					<Card.Body>
						<h5 className='mb-3'>
							<i className='fa-sharp fa-solid fa-check-circle me-2' style={{ color: '#28a745' }}></i>
							Setup Complete!
						</h5>
						<div className='mb-3'>
							<Badge bg='success' className='me-2 p-2'>
								{results.realStudentsMarked} real students marked
							</Badge>
							<Badge bg='info' className='p-2'>
								{results.testStudentsCreated} test students created
							</Badge>
						</div>
						<Alert variant='success'>
							<strong>What's next?</strong>
							<ul className='mb-0 mt-2'>
								<li>Go to the Students page to see the new filter options</li>
								<li>Filter by "Real Only" or "Test Only" to see your students</li>
								<li>Use batch edit to change student types if needed</li>
								<li>Delete test students when you no longer need them</li>
							</ul>
						</Alert>
					</Card.Body>
				</Card>
			)}

			<Card className='shadow-sm'>
				<Card.Body>
					<h5 className='mb-3'>
						<i className='fa-sharp fa-solid fa-users me-2'></i>
						Test Students to be Created:
					</h5>

					{!baseEmail && (
						<Alert variant='warning'>
							<i className='fa-sharp fa-solid fa-info-circle me-2'></i>
							Enter your email above to see personalized test student emails, or leave blank to use default test emails.
						</Alert>
					)}

					<div className='table-responsive'>
						<table className='table table-sm table-hover'>
							<thead>
								<tr>
									<th>Student</th>
									<th>Instrument</th>
									<th>Student Email</th>
									<th>Parent Email</th>
								</tr>
							</thead>
							<tbody>
								{fakeStudents.map((student, idx) => (
									<tr key={idx}>
										<td>
											<strong>{student.fullName}</strong>
											<br />
											<small className='text-muted'>{student.status}</small>
										</td>
										<td>{student.instrument}</td>
										<td>
											<code style={{ fontSize: '0.85em' }}>{student.email}</code>
										</td>
										<td>
											<code style={{ fontSize: '0.85em' }}>{student.parent1.email}</code>
											<br />
											<small className='text-muted'>{student.parent1.firstName} {student.parent1.lastName}</small>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{baseEmail && !emailError && (
						<Alert variant='info' className='mt-3'>
							<i className='fa-sharp fa-solid fa-inbox me-2'></i>
							<strong>All 10 email addresses</strong> (5 students + 5 parents) will deliver to <strong>{baseEmail}</strong>
						</Alert>
					)}

					<div className='d-grid gap-2 mt-4'>
						<Button
							variant='primary'
							size='lg'
							onClick={handleSetup}
							disabled={loading || (baseEmail && emailError)}
						>
							{loading ? (
								<>
									<i className='fa-sharp fa-solid fa-spinner fa-spin me-2'></i>
									Setting up...
								</>
							) : (
								<>
									<i className='fa-sharp fa-solid fa-play me-2'></i>
									{baseEmail ? 'Run Setup with Custom Emails' : 'Run Setup with Default Emails'}
								</>
							)}
						</Button>
					</div>
				</Card.Body>
			</Card>
		</Container>
	);
};

export default SetupStudentTypes;
